import React, { useEffect, useRef } from "react";
import FilterHeaderCell, { ActiveFilters } from "../../common/FilterHeaderCell";
import { activeFilterCount } from "../../common/columnFilters";
import { SCHEDULE_SECTIONS } from "../constants";
import { formatSince } from "../function";
import SyncCell from "./SyncCell";
import { RefreshIcon } from "./Icons";

// Device ID, Status, Name, then one Sync column per section.
const COLUMN_COUNT = 3 + SCHEDULE_SECTIONS.length;

const SkeletonRows = ({ count = 5 }) =>
  Array.from({ length: count }).map((unused, index) => (
    <tr key={index}>
      <td>
        <div className="ds-skeleton" style={{ width: "90px" }} />
      </td>
      <td>
        <div className="ds-skeleton" style={{ width: "120px" }} />
      </td>
      {SCHEDULE_SECTIONS.map((section) => (
        <td key={section.key}>
          <div className="ds-skeleton ds-skeleton-cell" />
        </td>
      ))}
    </tr>
  ));

const Message = ({ title, children }) => (
  <tr>
    <td className="ds-message" colSpan={COLUMN_COUNT}>
      <strong>{title}</strong>
      {children}
    </td>
  </tr>
);

// DeviceID | Name | one Sync column per configured section.
//
// Rows are banded by area. The band is not a column, so adding a section to the
// config still only widens the table by one.
// Online / Offline, from the device document (no call of its own). An offline
// device applies schedule changes only once it is back, which is why it is
// worth showing here.
const ConnectionStatus = ({ connection }) => {
  if (!connection || connection.online === null) return <span className="ds-unnamed">Unknown</span>;
  const { online, since } = connection;
  return (
    <div
      className={online ? "ds-conn ds-conn-online" : "ds-conn ds-conn-offline"}
      title={
        online
          ? `Online since ${formatSince(since)}`
          : `Offline since ${formatSince(since)}. Schedule changes apply when it comes back online.`
      }
    >
      <span className="ds-conn-dot" />
      {online ? "Online" : `Offline · ${formatSince(since)}`}
    </div>
  );
};

function DeviceScheduleTable({
  groups,
  loading,
  hasProperty,
  hasDevices,
  // Devices in the property not listed because they are not set to be
  // scheduled; tells "no devices" apart from "none set to be scheduled".
  unscheduledCount = 0,
  searching,
  // The header filters: { specs, values, options, onChange, onClear }.
  filters,
  savingKey,
  onEdit,
  refreshingIds = {},
  onRefreshDevice,
  // The appliance picked on the dashboard: its row is marked and scrolled to.
  highlightId,
}) {
  const highlightRef = useRef(null);
  const scrolledTo = useRef(null);

  // Scrolled to once per appliance, when its row first appears - not again
  // after every refresh or save.
  useEffect(() => {
    if (loading || !highlightId || scrolledTo.current === highlightId) return;
    if (!highlightRef.current) return;
    scrolledTo.current = highlightId;
    highlightRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlightId, loading, groups]);

  const filterCount = filters ? activeFilterCount(filters.values) : 0;

  const body = () => {
    if (loading) return <SkeletonRows />;

    if (!hasProperty) {
      return (
        <Message title="No property selected">
          Pick a property above to list its EnergiSync devices.
        </Message>
      );
    }

    if (!hasDevices && unscheduledCount > 0) {
      return (
        <Message title="No devices set to be scheduled">
          {unscheduledCount} device{unscheduledCount === 1 ? " is" : "s are"} in this property, but
          none is set to be scheduled. Set "To be scheduled" to Yes in Device Master to list
          them here.
        </Message>
      );
    }

    if (!hasDevices) {
      return (
        <Message title="No EnergiSync devices">
          This property has no EnergiSync devices assigned to it.
        </Message>
      );
    }

    if (groups.length === 0 && filterCount > 0) {
      return (
        <Message title="Nothing matches these filters">
          {searching
            ? `No device matches "${searching}" with these filters.`
            : "Change or clear the filters above the table to see devices."}
        </Message>
      );
    }

    if (groups.length === 0) {
      return (
        <Message title="Nothing matches that search">
          {searching
            ? `No device id, name or area matches "${searching}".`
            : "No devices to show."}
        </Message>
      );
    }

    return groups.map((group) => (
      <React.Fragment key={group.area}>
        <tr className="ds-area-band">
          <td colSpan={COLUMN_COUNT}>
            {group.area}
            <span className="ds-area-count">
              {group.rows.length} device{group.rows.length === 1 ? "" : "s"}
            </span>
          </td>
        </tr>

        {group.rows.map((row) => (
          <tr
            key={row.deviceId}
            ref={row.deviceId === highlightId ? highlightRef : undefined}
            className={row.deviceId === highlightId ? "ds-row-current" : undefined}
          >
            <td className="ds-device-id">
              {row.deviceId}
              {row.deviceId === highlightId && (
                <span className="ds-current-tag">On dashboard</span>
              )}
            </td>
            <td>
              <ConnectionStatus connection={row.connection} />
            </td>
            <td className="ds-device-name">
              <div className="ds-device-name-inner">
                {row.name || <span className="ds-unnamed">Unnamed device</span>}
                <button
                  type="button"
                  className="ds-row-refresh"
                  onClick={() => onRefreshDevice(row.deviceId)}
                  disabled={Boolean(refreshingIds[row.deviceId])}
                  data-tooltip={
                    refreshingIds[row.deviceId] ? "Refreshing..." : "Refresh this device"
                  }
                  aria-label={`Refresh ${row.deviceId}`}
                >
                  <span className={refreshingIds[row.deviceId] ? "ds-spin" : ""}>
                    <RefreshIcon size={14} />
                  </span>
                </button>
              </div>
            </td>

            {SCHEDULE_SECTIONS.map((section) => (
              <td key={section.key}>
                <SyncCell
                  section={section}
                  cell={row.cells[section.key]}
                  offline={row.connection?.online === false}
                  saving={savingKey === `${row.deviceId}:${section.key}`}
                  onEdit={() => onEdit(row, section)}
                />
              </td>
            ))}
          </tr>
        ))}
      </React.Fragment>
    ));
  };

  return (
    <>
      {filters && (
        <ActiveFilters
          specs={filters.specs}
          labels={Object.fromEntries(filters.specs.map(({ field, label }) => [field, label]))}
          filters={filters.values}
          options={filters.options}
          onChange={filters.onChange}
          onClear={filters.onClear}
        />
      )}
      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              {filters
                ? filters.specs.map(({ field, kind, label }) => (
                    <FilterHeaderCell
                      key={field}
                      label={label}
                      kind={kind}
                      value={filters.values[field]}
                      options={filters.options[field]}
                      onChange={(value) => filters.onChange(field, value)}
                    />
                  ))
                : [
                    "Device ID",
                    "Status",
                    "Name",
                    ...SCHEDULE_SECTIONS.map((section) => `${section.label} Sync`),
                  ].map((label) => <th key={label}>{label}</th>)}
            </tr>
          </thead>
          <tbody>{body()}</tbody>
        </table>
      </div>
    </>
  );
}

export default DeviceScheduleTable;
